/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import GenericButton from "@ui-components/Button/GenericButton";
import GenericModal from "@ui-components/Modal/GenericModal";
import { ReactComponent as Eye } from "@assets/Icons/Icon_Eye.svg";
import { ReactComponent as EyeSlash } from "@assets/Icons/Icon_EyeSlash.svg";
import { ReactComponent as Copy } from "@assets/Icons/IconCopy.svg";
import { ReactComponent as Info } from "@assets/Icons/Iconinformation.svg"
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Fwaas } from "./FwaasData";
import { fetchCredentials } from "./request/request";
import { AxiosInstance } from "axios";
import GenericInput from "@ui-components/Input/GenericInput";

type Props = {
  isOpen: boolean;
  row: Fwaas | null;
  onClose: () => void;
  api: AxiosInstance;
  fmUrl: string | null;
  isFetchingFm: boolean;
  tenantId: number;
  /** When set, replaces the default FWaaS management instructions in the banner. */
  managementBannerText?: string;
};

type FmCredItem = {
  id: number;
  tenantId: number;
  user: string;
  password?: string;
  createdAt?: string;
  updatedAt?: string;
};

type FmCredsResponse = {
  success?: boolean;
  message?: string;
  data?: {
    items?: FmCredItem[];
  };
};

const DEFAULT_MANAGEMENT_BANNER =
  "To review your Firewall-as-a-Service (FWaaS) configuration, open your administration module. This will launch a new tab where you can view your firewall settings. " +
  "If your role allows, you may also make changes from that page. Please use the username and password provided below this message to sign in.";

export default function CredentialsModal({
  isOpen,
  row,
  onClose,
  fmUrl,
  isFetchingFm,
  api,
  tenantId,
  managementBannerText,
}: Props) {

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [hasCredentials, setHasCredentials] = useState(false);

  const [isLoadingCreds, setIsLoadingCreds] = useState(false);
  const [credsError, setCredsError] = useState<string | null>(null);

  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState<"user" | "pass" | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    (async () => {
      try {
        setIsLoadingCreds(true);
        setCredsError(null);
        const res = await fetchCredentials(api, tenantId);
        const payload: FmCredsResponse = res?.data ?? {};
        const items = payload?.data?.items ?? [];
        const first = items[0];

        if (!cancelled) {
          if (first) {
            setUsername(first.user ?? "");
            setPassword(first.password ?? "");
            setHasCredentials(true);
          } else {
            setUsername("");
            setPassword("");
            setHasCredentials(false);
          }
        }
      } catch (e: any) {
        if (!cancelled) {
          setCredsError(e?.message || "Failed to load credentials.");
          setUsername("");
          setPassword("");
          setHasCredentials(false);
        }
      } finally {
        if (!cancelled) setIsLoadingCreds(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, api, tenantId]);

  const handleOpenServiceMgmt = useCallback(() => {
    if (!fmUrl) return;
    window.open(fmUrl, "_blank", "noopener,noreferrer");
  }, [fmUrl]);

  const handleCopy = useCallback((text: string, which: "user" | "pass") => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(which);
      setTimeout(() => setCopied(null), 1200);
    });
  }, []);

  const serviceBtnDisabled = useMemo(
    () => isFetchingFm || !fmUrl || isLoadingCreds || !hasCredentials,
    [isFetchingFm, fmUrl, isLoadingCreds, hasCredentials]
  );

  // --- Bailout AFTER hooks are set up ---
  if (!isOpen && !row) return null;

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span className="text-lg font-semibold text-semantic-text-primary">
          Management
        </span>
      }
    >
      {/* Banner */}
      <div className="rounded-2xl border border-semantic-border-brand-secondary bg-semantic-bg-brand-primary p-4 my-10 flex items-start gap-3">
        <div className="mt-0.5">
          <Info className="w-5 h-5 text-semantic-text-brand" />
        </div>
        <div className="flex-1 text-sm text-semantic-text-brand">
          {managementBannerText ?? DEFAULT_MANAGEMENT_BANNER}
        </div>
        <div>
          <GenericButton
            size="sm"
            variant="secondary"
            onClick={handleOpenServiceMgmt}
            label={
              isFetchingFm || isLoadingCreds ? "Loading..." : "Access"
            }
            disabled={serviceBtnDisabled}
          />
        </div>
      </div>
      {credsError && (
          <div className="text-sm text-semantic-text-danger">{credsError}</div>
        )}

      {/* Pending / service not active empty state */}
      {!isLoadingCreds && !hasCredentials && !credsError && (
        <div className="rounded-2xl border border-semantic-border-secondary bg-semantic-bg-secondary px-6 py-8 flex flex-col items-center text-center gap-2">
          <div className="w-10 h-10 rounded-full bg-semantic-bg-brand-primary flex items-center justify-center">
            <Info className="w-5 h-5 text-semantic-text-brand" />
          </div>
          <div className="text-base font-semibold text-semantic-text-primary">
            Service not yet fully active
          </div>
          <div className="text-sm text-semantic-text-secondary max-w-md">
            Once the service is fully active, credentials will be available
            here.
          </div>
        </div>
      )}

      {/* Credentials */}
      {(isLoadingCreds || hasCredentials) && (
      <div className="space-y-4">
        <span className="text-lg font-semibold text-semantic-text-primary mb-5">
          Credentials
        </span>
        {isLoadingCreds ? (
          <div className="space-y-4">
          <div>
            <div className="h-4 w-20 bg-gray-200 rounded mb-2" />
            <div className="h-9 w-full bg-gray-200 rounded animate-pulse" />
          </div>
          <div>
            <div className="h-4 w-20 bg-gray-200 rounded mb-2" />
            <div className="h-9 w-full bg-gray-200 rounded animate-pulse" />
          </div>
          </div>
        ) : (
          <>
        {/* Username */}
        <div className="relative">
          <GenericInput
            label="Username"
            value={username}
            onChange={setUsername}
            type="text"
            disabled={true}
            readOnly={true}
            className="[&>div:last-child>input]:pr-24"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2" style={{ marginTop: '0.75rem' }}>
            <GenericButton
              size="sm"
              variant="secondary"
              onClick={() => handleCopy(username, "user")}
              label={copied === "user" ? "Copied!" : "Copy"}
              icon={<Copy className="w-3.5 h-3.5" />}
              iconPosition="left"
            />
          </div>
        </div>

        {/* Password */}
        <div className="relative">
          <GenericInput
            label="Password"
            value={password}
            onChange={setPassword}
            type={showPassword ? "text" : "password"}
            disabled={true}
            readOnly={true}
            className="[&>div:last-child>input]:pr-40 keeper-ignore"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-2" style={{ marginTop: '0.75rem' }}>
            <GenericButton
              size="sm"
              variant="secondary"
              onClick={() => setShowPassword((s) => !s)}
              label={showPassword ? "Hide" : "Show"}
              icon={showPassword ? <EyeSlash className="w-4 h-4.5" /> : <Eye className="w-4 h-4.5" />}
              iconPosition="left"
            />
            <GenericButton
              size="sm"
              variant="secondary"
              onClick={() => handleCopy(password, "pass")}
              label={copied === "pass" ? "Copied!" : "Copy"}
              icon={<Copy className="w-3.5 h-3.5" />}
              iconPosition="left"
            />
          </div>
        </div>
        </>
        )}
      </div>
      )}

      {/* Actions */}
      <div className="mt-6 flex justify-end space-x-2">
        <GenericButton
          size="sm"
          variant="secondary"
          onClick={onClose}
          label="Close"
        />
      </div>
    </GenericModal>
  );
}